import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Download, Plus, Trash2, Pencil, Check, X, Upload, ChevronDown, ChevronUp } from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtCI = (n) => `CI$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PayCycles = () => {
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');

  const [cycles, setCycles] = useState([]);
  const [historical, setHistorical] = useState([]);
  const [defaultTarget, setDefaultTarget] = useState(0);
  const [defaultTargetInput, setDefaultTargetInput] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // cycle_start being edited
  const [editingTarget, setEditingTarget] = useState(null); // cycle_start being target-edited
  const [targetForm, setTargetForm] = useState('');
  const [form, setForm] = useState({ cycle_start: '', total_revenue: '', note: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // --- Bulk import ---
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkReplace, setBulkReplace] = useState(true);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    if (!password) { navigate('/admin/login'); return; }
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Basic ${btoa(':' + password)}` };
      const [cRes, hRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/analytics/pay-cycles?cycles=24`, { headers }),
        fetch(`${API_URL}/api/admin/analytics/pay-cycles/historical`, { headers }),
      ]);
      if (cRes.ok) {
        const j = await cRes.json();
        setCycles(j.cycles || []);
        setDefaultTarget(j.default_target || 0);
        setDefaultTargetInput(j.default_target ? String(j.default_target) : '');
      }
      if (hRes.ok) setHistorical((await hRes.json()).items || []);
    } catch (e) {
      logger.error('Pay cycles fetch failed:', e);
    } finally {
      setLoading(false);
    }
  };

  const historicalMap = useMemo(() => {
    const m = {};
    for (const h of historical) m[h.cycle_start] = h;
    return m;
  }, [historical]);

  // NB: backend returns the cycle's start date as `cycle_key` (YYYY-MM-DD).
  // Use `cycle_key` everywhere — the historical-entry API expects the same
  // string under the name `cycle_start`.
  const startEdit = (cycle) => {
    const existing = historicalMap[cycle.cycle_key];
    setEditing(cycle.cycle_key);
    setForm({
      cycle_start: cycle.cycle_key,
      total_revenue: existing ? String(existing.total_revenue) : '',
      note: existing?.note || '',
    });
    setError('');
  };

  const cancelEdit = () => { setEditing(null); setForm({ cycle_start: '', total_revenue: '', note: '' }); };

  const saveHistorical = async () => {
    setError('');
    const amount = parseFloat(form.total_revenue);
    if (!form.cycle_start || isNaN(amount) || amount < 0) {
      setError('Please enter a valid non-negative total revenue.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics/pay-cycles/historical`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
        body: JSON.stringify({ cycle_start: form.cycle_start, total_revenue: amount, note: form.note || null }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Save failed');
      }
      await fetchAll();
      cancelEdit();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteHistorical = async (cycleStart) => {
    if (!window.confirm(`Remove historical revenue for cycle starting ${cycleStart}?`)) return;
    try {
      await fetch(`${API_URL}/api/admin/analytics/pay-cycles/historical/${cycleStart}`, {
        method: 'DELETE',
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      await fetchAll();
    } catch (e) {
      logger.error('Delete failed', e);
    }
  };

  const downloadCsv = (cycleStart) => {
    const url = `${API_URL}/api/admin/analytics/pay-cycles/${cycleStart}/export.csv`;
    fetch(url, { headers: { Authorization: `Basic ${btoa(':' + password)}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `pay-cycle-${cycleStart}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  const saveDefaultTarget = async () => {
    const v = parseFloat(defaultTargetInput);
    if (isNaN(v) || v < 0) { setError('Default target must be ≥ 0'); return; }
    setSavingDefault(true);
    try {
      await fetch(`${API_URL}/api/admin/analytics/pay-cycles/target`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
        body: JSON.stringify({ target: v }),
      });
      await fetchAll();
    } finally {
      setSavingDefault(false);
    }
  };

  const startEditTarget = (cycle) => {
    setEditingTarget(cycle.cycle_key);
    setTargetForm(cycle.target ? String(cycle.target) : '');
  };
  const cancelEditTarget = () => { setEditingTarget(null); setTargetForm(''); };

  const saveCycleTarget = async (cycleStart) => {
    const v = parseFloat(targetForm) || 0;
    if (v < 0) return;
    try {
      await fetch(`${API_URL}/api/admin/analytics/pay-cycles/${cycleStart}/target`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
        body: JSON.stringify({ target: v }),
      });
      await fetchAll();
      cancelEditTarget();
    } catch (e) {
      logger.error('Set target failed', e);
    }
  };

  // Tailwind classes per status
  const statusBg = {
    on_track: 'bg-emerald-50/60',
    near: 'bg-amber-50/60',
    behind: 'bg-red-50/40',
    no_target: '',
  };
  const statusBadge = {
    on_track: 'bg-emerald-200 text-emerald-900',
    near: 'bg-amber-200 text-amber-900',
    behind: 'bg-red-200 text-red-900',
  };
  const statusLabel = { on_track: 'On track', near: 'Near', behind: 'Behind' };

  // Parse pasted spreadsheet text into bulk-import rows.
  // Supports comma, tab, semicolon separators. Skips blank/comment lines.
  // Each line: cycle_start [, total_revenue [, note]]
  // cycle_start can be "YYYY-MM-DD" (must be the 16th) or "YYYY-MM" / "Mon YYYY"
  // (auto-converted to the 16th of that month).
  const parseBulkText = (text) => {
    const monthMap = {
      jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06',
      jul: '07', aug: '08', sep: '09', sept: '09', oct: '10', nov: '11', dec: '12',
      january: '01', february: '02', march: '03', april: '04', june: '06',
      july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
    };
    const out = [];
    const errors = [];
    const lines = text.split(/\r?\n/);
    lines.forEach((rawLine, i) => {
      const line = rawLine.trim();
      if (!line || line.startsWith('#')) return;
      // Skip header rows containing words
      if (i === 0 && /^[a-z][a-z\s_-]*$/i.test(line.split(/[,\t;]/)[0]?.trim() || '') && !/\d/.test(line)) {
        return;
      }
      const parts = line.split(/\s*[,\t;]\s*/);
      if (parts.length < 2) {
        errors.push(`Line ${i + 1}: expected at least "cycle_start, total_revenue"`);
        return;
      }
      let [datePart, amountPart, ...noteParts] = parts;
      let cycleStart = '';

      // Try YYYY-MM-DD
      const isoMatch = datePart.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (isoMatch) {
        const [, y, m, d] = isoMatch;
        cycleStart = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
      } else {
        // YYYY-MM → assume 16th
        const ymMatch = datePart.match(/^(\d{4})-(\d{1,2})$/);
        if (ymMatch) {
          cycleStart = `${ymMatch[1]}-${ymMatch[2].padStart(2, '0')}-16`;
        } else {
          // "Mon YYYY" or "YYYY Mon"
          const tokens = datePart.toLowerCase().split(/\s+/);
          const y = tokens.find((t) => /^\d{4}$/.test(t));
          const m = tokens.find((t) => monthMap[t]);
          if (y && m) cycleStart = `${y}-${monthMap[m]}-16`;
        }
      }
      if (!cycleStart) {
        errors.push(`Line ${i + 1}: couldn't parse date "${datePart}"`);
        return;
      }
      // Force 16th
      cycleStart = cycleStart.slice(0, 8) + '16';
      const amount = parseFloat(amountPart.replace(/[^\d.-]/g, ''));
      if (isNaN(amount) || amount < 0) {
        errors.push(`Line ${i + 1}: invalid amount "${amountPart}"`);
        return;
      }
      out.push({ cycle_start: cycleStart, total_revenue: amount, note: noteParts.join(', ').trim() || null });
    });
    return { items: out, errors };
  };

  const submitBulk = async () => {
    setBulkResult(null);
    const parsed = parseBulkText(bulkText);
    if (parsed.errors.length) {
      setBulkResult({ parseErrors: parsed.errors });
      return;
    }
    if (!parsed.items.length) {
      setBulkResult({ parseErrors: ['No rows to import.'] });
      return;
    }
    setBulkSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/analytics/pay-cycles/historical/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Basic ${btoa(':' + password)}` },
        body: JSON.stringify({ items: parsed.items, replace_existing: bulkReplace }),
      });
      const json = await res.json();
      setBulkResult(json);
      if (res.ok && (json.inserted + json.updated) > 0) {
        await fetchAll();
      }
    } catch (e) {
      logger.error('Bulk import failed', e);
      setBulkResult({ parseErrors: [String(e.message || e)] });
    } finally {
      setBulkSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">Pay Cycles</h1>
              <p className="text-sm text-foreground/60">Revenue per pay cycle (16th to 15th of the next month)</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            data-testid="back-to-dashboard-btn"
            className="px-4 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 space-y-6">
        {/* Helper banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
          A pay cycle runs from the <strong>16th of one month through the 15th of the next</strong>. Cycles with no payments yet show CI$0.00 — click any past cycle's <Pencil className="w-3 h-3 inline" /> to record a historical revenue total. Real payments always take precedence over manual entries.
        </div>

        {/* Default target form */}
        <div className="bg-white rounded-xl border border-border shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="default-target-form">
          <div className="flex-1 min-w-[200px]">
            <p className="text-sm font-semibold text-foreground">Default cycle target</p>
            <p className="text-xs text-foreground/60">
              Applied to every cycle unless a per-cycle override is set. Rows highlight green when on track, amber when within 25% behind, red when ≥25% behind. The current cycle is pro-rated by elapsed time so it isn't flagged red on day 1.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-foreground/70">CI$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={defaultTargetInput}
              onChange={(e) => setDefaultTargetInput(e.target.value)}
              data-testid="default-target-input"
              placeholder="0.00 (leave 0 to disable)"
              className="w-40 px-2 py-1 border border-border rounded text-sm"
            />
            <button
              onClick={saveDefaultTarget}
              disabled={savingDefault}
              data-testid="save-default-target-btn"
              className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
            >
              {savingDefault ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {/* Bulk import historical revenues */}
        <div className="bg-white rounded-xl border border-border shadow-sm" data-testid="bulk-import-panel">
          <button
            onClick={() => setBulkOpen((v) => !v)}
            data-testid="bulk-import-toggle"
            className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
          >
            <div className="flex items-center gap-2 text-foreground">
              <Upload className="w-4 h-4 text-primary" />
              <span className="font-semibold text-sm">Bulk import historical revenues</span>
              <span className="text-xs text-foreground/60">(paste from a spreadsheet)</span>
            </div>
            {bulkOpen ? <ChevronUp className="w-4 h-4 text-foreground/60" /> : <ChevronDown className="w-4 h-4 text-foreground/60" />}
          </button>
          {bulkOpen && (
            <div className="px-4 pb-4 border-t border-border">
              <div className="text-xs text-foreground/60 mt-3 mb-2">
                One row per cycle. Accepted formats:
                <span className="ml-1 font-mono bg-gray-100 px-1 rounded">YYYY-MM-DD, amount, note</span>
                {' · '}
                <span className="font-mono bg-gray-100 px-1 rounded">YYYY-MM, amount</span>
                {' · '}
                <span className="font-mono bg-gray-100 px-1 rounded">Mon YYYY, amount</span>.
                Comma, tab, or semicolon separators are all fine.
                Dates are auto-shifted to the 16th of the matching month.
              </div>
              <textarea
                value={bulkText}
                onChange={(e) => setBulkText(e.target.value)}
                rows={6}
                placeholder={`2025-12-16, 4250, December cycle\n2025-11-16, 3800\nOct 2025\t5100\tStrong month`}
                data-testid="bulk-textarea"
                className="w-full px-3 py-2 border border-border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <label className="flex items-center gap-2 text-xs text-foreground/70">
                  <input
                    type="checkbox"
                    checked={bulkReplace}
                    onChange={(e) => setBulkReplace(e.target.checked)}
                    data-testid="bulk-replace-checkbox"
                  />
                  Replace existing entries (otherwise skip rows that already exist)
                </label>
                <div className="flex-1" />
                <button
                  onClick={submitBulk}
                  disabled={bulkSubmitting || !bulkText.trim()}
                  data-testid="bulk-submit-btn"
                  className="px-4 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
                >
                  {bulkSubmitting ? 'Importing…' : 'Import'}
                </button>
              </div>
              {bulkResult && (
                <div className="mt-3 text-xs" data-testid="bulk-result">
                  {bulkResult.parseErrors ? (
                    <div className="bg-red-50 border border-red-200 rounded p-2 text-red-800">
                      <div className="font-semibold mb-1">Could not parse:</div>
                      <ul className="list-disc ml-4 space-y-0.5">
                        {bulkResult.parseErrors.map((m, i) => <li key={i}>{m}</li>)}
                      </ul>
                    </div>
                  ) : (
                    <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-900">
                      <div className="font-semibold mb-1">
                        ✓ {bulkResult.inserted} inserted · {bulkResult.updated} updated
                        {bulkResult.skipped > 0 && ` · ${bulkResult.skipped} skipped`}
                      </div>
                      {bulkResult.results?.some((r) => !r.ok) && (
                        <ul className="list-disc ml-4 mt-1 text-amber-800">
                          {bulkResult.results.filter((r) => !r.ok).map((r, i) =>
                            <li key={i}>Row {r.row} ({r.cycle_start}): {r.error}</li>
                          )}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm overflow-hidden" data-testid="pay-cycles-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-foreground/60 uppercase">Cycle</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase" title="Gross revenue minus refunds issued in this cycle">Total (net)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Target</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground/60 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Invoices</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Quote Pay</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Receipts</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-red-600 uppercase">Refunds</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-red-600 uppercase" title="Business expenses recorded in this cycle">Expenses</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-purple-700 uppercase" title="Total − Expenses">Net Income</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-foreground/60 uppercase"># Pay</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-foreground/60 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {cycles.map((c) => {
                  const isEditing = editing === c.cycle_key;
                  const rowClass = c.is_current
                    ? 'bg-amber-50/40'
                    : c.historical_override
                      ? 'bg-blue-50/30'
                      : '';
                  if (isEditing) {
                    return (
                      <tr key={c.cycle_key} className="bg-blue-50">
                        <td className="px-4 py-3 font-medium text-foreground">{c.label}</td>
                        <td colSpan={10} className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs text-foreground/60">Total revenue (CI$):</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              autoFocus
                              data-testid="historical-total-input"
                              value={form.total_revenue}
                              onChange={(e) => setForm({ ...form, total_revenue: e.target.value })}
                              className="w-32 px-2 py-1 border border-border rounded text-sm"
                              placeholder="0.00"
                            />
                            <input
                              type="text"
                              data-testid="historical-note-input"
                              value={form.note}
                              onChange={(e) => setForm({ ...form, note: e.target.value })}
                              className="flex-1 min-w-[160px] px-2 py-1 border border-border rounded text-sm"
                              placeholder="Note (optional)"
                            />
                            {error && <span className="text-xs text-red-600 w-full">{error}</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={saveHistorical}
                              disabled={saving}
                              data-testid="save-historical-btn"
                              className="p-1.5 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                            <button onClick={cancelEdit} className="p-1.5 border border-border rounded hover:bg-gray-50">
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  }
                  const isEditingTarget = editingTarget === c.cycle_key;
                  return (
                    <tr
                      key={c.cycle_key}
                      className={(statusBg[c.status] || '') + ' ' + rowClass + ' hover:bg-gray-50'}
                      data-testid={`pay-cycle-row-${c.cycle_key}`}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">
                        {c.label}
                        {c.is_current && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-amber-200 text-amber-900 rounded">In Progress</span>
                        )}
                        {c.historical_override && (
                          <span className="ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide bg-blue-200 text-blue-900 rounded">Historical</span>
                        )}
                        {c.note && <div className="text-[11px] text-foreground/50 mt-0.5">{c.note}</div>}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-foreground">{fmtCI(c.total)}</td>
                      <td className="px-4 py-3 text-right">
                        {isEditingTarget ? (
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              autoFocus
                              data-testid="cycle-target-input"
                              value={targetForm}
                              onChange={(e) => setTargetForm(e.target.value)}
                              className="w-24 px-1 py-0.5 border border-border rounded text-xs"
                              placeholder="0.00"
                            />
                            <button
                              onClick={() => saveCycleTarget(c.cycle_key)}
                              data-testid="save-cycle-target-btn"
                              className="p-1 bg-primary text-white rounded"
                            ><Check className="w-3 h-3" /></button>
                            <button onClick={cancelEditTarget} className="p-1 border border-border rounded">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => startEditTarget(c)}
                            data-testid={`edit-target-${c.cycle_key}`}
                            className="text-foreground/70 hover:text-primary"
                            title={c.target ? 'Edit per-cycle target' : 'Set per-cycle target (overrides default)'}
                          >
                            {c.target > 0 ? fmtCI(c.target) : <span className="text-foreground/40">—</span>}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {c.target_pct != null ? (
                          <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wide rounded font-semibold ${statusBadge[c.status] || 'bg-gray-200 text-gray-700'}`}>
                            {statusLabel[c.status] || '—'} · {c.target_pct.toFixed(0)}%
                          </span>
                        ) : (
                          <span className="text-[10px] text-foreground/40">No target</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-foreground/70">{fmtCI(c.invoice_revenue)}</td>
                      <td className="px-4 py-3 text-right text-foreground/70">{fmtCI(c.quote_payment_revenue)}</td>
                      <td className="px-4 py-3 text-right text-foreground/70">{fmtCI(c.receipt_revenue)}</td>
                      <td className="px-4 py-3 text-right" data-testid={`refund-cell-${c.cycle_key}`}>
                        {c.refund_total > 0
                          ? <span className="text-red-600 font-medium">−{fmtCI(c.refund_total)}</span>
                          : <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right" data-testid={`expenses-cell-${c.cycle_key}`}>
                        {c.expenses > 0
                          ? <span className="text-red-600">−{fmtCI(c.expenses)}</span>
                          : <span className="text-foreground/30">—</span>}
                      </td>
                      <td className="px-4 py-3 text-right font-bold" data-testid={`net-income-cell-${c.cycle_key}`}>
                        <span className={c.net_income < 0 ? 'text-red-700' : 'text-purple-800'}>
                          {fmtCI(c.net_income || 0)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-foreground/70">{c.payments_count}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {c.payments_count > 0 && (
                            <button
                              onClick={() => downloadCsv(c.cycle_key)}
                              data-testid={`download-csv-${c.cycle_key}`}
                              title="Download CSV of this cycle's payments"
                              className="p-1.5 text-primary hover:bg-primary/10 rounded"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          {!c.is_current && (
                            <button
                              onClick={() => startEdit(c)}
                              data-testid={`edit-historical-${c.cycle_key}`}
                              title={c.historical_override ? 'Edit historical entry' : 'Add historical entry'}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            >
                              {c.historical_override ? <Pencil className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                            </button>
                          )}
                          {c.historical_override && (
                            <button
                              onClick={() => deleteHistorical(c.cycle_key)}
                              data-testid={`delete-historical-${c.cycle_key}`}
                              title="Remove historical entry"
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayCycles;
